import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, Pencil, Trash2, Download, Loader2, X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE_WIDE, DataTable, SearchInput, FilterChips, OverflowMenu, Dialog, DetailDrawer, Field, FormRow,
  Switch, StatusPill, money, fmtDate, fmtDayMonth, num, plural,
} from '../../components/ui';

// Service agreements: the NDIS service agreement register. One status per row, worked out from
// what was saved and the end date, so a row never says "active" and "expired" at once.

const ymd = (v) => (v ? String(v).split('T')[0] : '');
function daysUntil(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd(v));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - t.getTime()) / 86400000);
}

const EXPIRING_DAYS = 60;
const AGREEMENT_STATUS = {
  draft:     { label: 'Draft', tone: 'neutral' },
  active:    { label: 'Active', tone: 'success' },
  expiring:  { label: 'Expiring', tone: 'warning' },
  expired:   { label: 'Expired', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

// Draft and cancelled are what was saved; otherwise the end date decides: past = expired, within
// 60 days = expiring, else active.
function displayStatus(a) {
  const s = String(a.status || 'active').toLowerCase();
  if (s === 'draft' || s === 'cancelled') return s;
  if (s === 'expired') return 'expired';
  const d = daysUntil(a.end_date);
  if (d != null && d < 0) return 'expired';
  if (d != null && d <= EXPIRING_DAYS) return 'expiring';
  return 'active';
}

function endsText(a) {
  const d = daysUntil(a.end_date);
  if (d == null) return null;
  if (d < 0) return { text: d === -1 ? 'Ended yesterday' : `Ended ${plural(-d, 'day')} ago`, tone: 'text-slate-500' };
  if (d === 0) return { text: 'Ends today', tone: 'text-amber-700' };
  if (d <= EXPIRING_DAYS) return { text: `Ends in ${plural(d, 'day')}`, tone: 'text-amber-700' };
  const months = Math.round(d / 30.4);
  return { text: `Ends in ${months} mo`, tone: 'text-slate-500' };
}

const periodText = (a) => {
  if (!a.start_date && !a.end_date) return '—';
  return `${a.start_date ? fmtDate(ymd(a.start_date)) : 'No start'} – ${a.end_date ? fmtDate(ymd(a.end_date)) : 'No end'}`;
};
const signedText = (a) => (a.signed_by_client ? (a.signed_date ? `Signed ${fmtDayMonth(ymd(a.signed_date))}` : 'Signed') : 'Awaiting signature');

// A one-page summary of the agreement and its support items, on the organisation's letterhead.
async function downloadAgreementPdf(agreement) {
  const [{ default: JsPDF }, { default: autoTable }, branding] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    import('../../utils/exportBranding').then((m) => m.getBranding().then((b) => ({ b, m }))).catch(() => ({ b: null, m: null })),
  ]);
  const doc = new JsPDF({ unit: 'mm', format: 'a4' });
  const title = `Service agreement${agreement.agreement_number ? ` ${agreement.agreement_number}` : ''}`;
  let y = branding.m
    ? branding.m.renderPdfHeader(doc, branding.b, { reportTitle: title, subtitle: 'Summary of the agreement on record' })
    : 20;
  if (!branding.m) { doc.setFontSize(15); doc.text(title, 14, y); y += 8; }

  autoTable(doc, {
    startY: y,
    theme: 'plain',
    styles: { fontSize: 9.5, cellPadding: { top: 1.8, bottom: 1.8, left: 0, right: 4 } },
    columnStyles: { 0: { cellWidth: 42, textColor: [100, 116, 139] }, 1: { textColor: [30, 41, 59] } },
    body: [
      ['Participant', agreement.client_name || '—'],
      ['Agreement number', agreement.agreement_number || '—'],
      ['Period', periodText(agreement)],
      ['Total value', money(agreement.total_value || 0)],
      ['Status', AGREEMENT_STATUS[displayStatus(agreement)].label],
      ['Signed', agreement.signed_by_client ? `Yes${agreement.signed_date ? `, ${fmtDate(ymd(agreement.signed_date))}` : ''}` : 'Not yet signed'],
    ],
  });
  y = doc.lastAutoTable.finalY + 6;
  if (agreement.notes) {
    doc.setFontSize(10); doc.setTextColor(30, 41, 59); doc.setFont('helvetica', 'bold');
    doc.text('Notes', 14, y); y += 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(51, 65, 85);
    const lines = doc.splitTextToSize(String(agreement.notes), doc.internal.pageSize.getWidth() - 28);
    doc.text(lines, 14, y); y += lines.length * 4.6 + 4;
  }
  const items = agreement.items || [];
  autoTable(doc, {
    startY: y,
    head: [['Support item', 'Item number', 'Hours / week', 'Rate', 'Total']],
    body: items.length ? items.map((it) => [
      it.support_item_name || '—', it.support_item_number || '—',
      it.hours_per_week != null && it.hours_per_week !== '' ? num(it.hours_per_week, 1) : '—',
      it.rate != null && it.rate !== '' ? money(it.rate) : '—',
      it.total_amount != null && it.total_amount !== '' ? money(it.total_amount) : '—',
    ]) : [[{ content: 'No support items recorded', colSpan: 5 }]],
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: [241, 245, 249], textColor: [51, 65, 85], fontStyle: 'bold' },
    columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
  });
  const safe = String(agreement.agreement_number || agreement.client_name || 'agreement').replace(/[^\w-]+/g, '_');
  doc.save(`service-agreement-${safe}.pdf`);
}

// ── Add / edit ───────────────────────────────────────────────────────────────
function AgreementDialog({ agreement, clients, onClose, onSaved }) {
  const isEdit = !!(agreement && agreement.id);
  const [form, setForm] = useState(() => (isEdit ? {
    clientId: agreement.client_id,
    agreementNumber: agreement.agreement_number || '',
    startDate: ymd(agreement.start_date),
    endDate: ymd(agreement.end_date),
    totalValue: agreement.total_value || 0,
    status: agreement.status || 'active',
    notes: agreement.notes || '',
    signedByClient: !!agreement.signed_by_client,
    signedDate: ymd(agreement.signed_date),
  } : {
    clientId: '', agreementNumber: '', startDate: '', endDate: '',
    totalValue: 0, status: 'active', notes: '', signedByClient: false, signedDate: '',
  }));
  const [saving, setSaving] = useState(false);
  const [clientError, setClientError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    if (e) e.preventDefault();
    if (!isEdit && !form.clientId) { setClientError('Choose the participant.'); return; }
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/service-agreements/${agreement.id}`, form); toast.success('Agreement updated'); }
      else { await api.post('/service-agreements', form); toast.success('Agreement created'); }
      onSaved();
      onClose();
    } catch (err) {
      toast.error((err && err.response && err.response.data && err.response.data.message) || 'Could not save the agreement');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} title={isEdit ? 'Edit service agreement' : 'New service agreement'}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" form="agreement-form" disabled={saving} className="btn-primary">
            {saving && <Loader2 size={14} className="animate-spin" />}{isEdit ? 'Save changes' : 'Create agreement'}
          </button>
        </>
      )}>
      <form id="agreement-form" onSubmit={save} className="space-y-4">
        <Field label="Participant" required={!isEdit} error={clientError}>
          {(id) => (
            <select id={id} className="input-field" value={form.clientId || ''} disabled={isEdit} aria-invalid={!!clientError}
              onChange={(e) => { set('clientId', e.target.value); setClientError(''); }}>
              <option value="">Choose a participant</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}{c.ndis_number ? ` (NDIS ${c.ndis_number})` : ''}</option>)}
            </select>
          )}
        </Field>
        <FormRow>
          <Field label="Agreement number">
            {(id) => <input id={id} className="input-field" value={form.agreementNumber || ''} onChange={(e) => set('agreementNumber', e.target.value)} />}
          </Field>
          <Field label="Status" hint="Expiring and expired are worked out from the end date.">
            {(id) => (
              <select id={id} className="input-field" value={form.status || 'active'} onChange={(e) => set('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            )}
          </Field>
          <Field label="Start date">
            {(id) => <input id={id} type="date" className="input-field" value={form.startDate || ''} onChange={(e) => set('startDate', e.target.value)} />}
          </Field>
          <Field label="End date">
            {(id) => <input id={id} type="date" className="input-field" value={form.endDate || ''} onChange={(e) => set('endDate', e.target.value)} />}
          </Field>
        </FormRow>
        <Field label="Total value ($)">
          {(id) => <input id={id} type="number" step="0.01" className="input-field tabular-nums" value={form.totalValue || ''} onChange={(e) => set('totalValue', e.target.value)} />}
        </Field>
        <div className="rounded-xl border border-slate-200 px-4 py-2.5 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <span className="text-[13px] font-medium text-slate-800">Signed by the participant</span>
            <Switch checked={!!form.signedByClient} onChange={(v) => set('signedByClient', v)} label="Signed by the participant" />
          </div>
          {form.signedByClient && (
            <Field label="Date signed">
              {(id) => <input id={id} type="date" className="input-field" value={form.signedDate || ''} onChange={(e) => set('signedDate', e.target.value)} />}
            </Field>
          )}
        </div>
        <Field label="Notes">
          {(id) => <textarea id={id} rows={3} className="input-field resize-y" value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />}
        </Field>
      </form>
    </Dialog>
  );
}

// ── Detail drawer with support items ─────────────────────────────────────────
const BLANK_ITEM = { supportItemName: '', supportItemNumber: '', hoursPerWeek: '', rate: '', totalHours: '', totalAmount: '' };

function AgreementDrawer({ agreement, onClose, onEdit, onDelete, onChanged }) {
  const confirm = useConfirm();
  const [addingItem, setAddingItem] = useState(false);
  const [itemForm, setItemForm] = useState(BLANK_ITEM);
  const [itemError, setItemError] = useState('');
  const [savingItem, setSavingItem] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const status = displayStatus(agreement);
  const items = agreement.items || [];

  const addItem = async (e) => {
    if (e) e.preventDefault();
    if (!itemForm.supportItemName.trim()) { setItemError('Enter the support item name.'); return; }
    setSavingItem(true);
    try {
      await api.post(`/service-agreements/${agreement.id}/items`, itemForm);
      toast.success('Support item added');
      setItemForm(BLANK_ITEM);
      setAddingItem(false);
      onChanged();
    } catch { toast.error('Could not add the support item'); }
    finally { setSavingItem(false); }
  };

  const delItem = async (it) => {
    if (!(await confirm(`"${it.support_item_name || it.support_item_number || 'This item'}" comes off this agreement.`,
      { title: 'Remove this support item?', confirmLabel: 'Remove item', danger: true }))) return;
    try {
      await api.delete(`/service-agreements/${agreement.id}/items/${it.id}`);
      toast.success('Support item removed');
      onChanged();
    } catch { toast.error('Could not remove the support item'); }
  };

  const download = async () => {
    setDownloading(true);
    try { await downloadAgreementPdf(agreement); } catch { toast.error('Could not create the PDF'); }
    finally { setDownloading(false); }
  };

  const setItem = (k, v) => { setItemForm((f) => ({ ...f, [k]: v })); if (k === 'supportItemName' && itemError) setItemError(''); };

  return (
    <DetailDrawer open onClose={onClose} width="lg" icon={FileText}
      title={`Agreement${agreement.agreement_number ? ` ${agreement.agreement_number}` : ''}`}
      subtitle={agreement.client_name}
      statusNode={<StatusPill status={status} map={AGREEMENT_STATUS} />}
      meta={[
        { label: 'Period', value: periodText(agreement) },
        { label: 'Total value', value: money(agreement.total_value || 0), money: true },
        { label: 'Signed', value: agreement.signed_by_client ? `Yes${agreement.signed_date ? `, ${fmtDate(ymd(agreement.signed_date))}` : ''}` : 'Awaiting signature' },
      ]}
      footer={(
        <>
          <button type="button" onClick={onDelete} className="btn-ghost text-rose-600 hover:bg-rose-50 hover:text-rose-700 mr-auto"><Trash2 size={15} /> Delete</button>
          <button type="button" onClick={download} disabled={downloading} className="btn-secondary">
            {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download PDF
          </button>
          <button type="button" onClick={onEdit} className="btn-primary"><Pencil size={15} /> Edit</button>
        </>
      )}>
      <div className="space-y-5">
        {agreement.notes && (
          <div>
            <h4 className="text-[12.5px] font-semibold text-slate-700 mb-1">Notes</h4>
            <p className="text-[13px] text-slate-700 whitespace-pre-wrap">{agreement.notes}</p>
          </div>
        )}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[13px] font-semibold text-slate-900">Support items <span className="text-slate-500 font-normal tabular-nums">{num(items.length)}</span></h4>
            {!addingItem && (
              <button type="button" onClick={() => { setAddingItem(true); setItemForm(BLANK_ITEM); }} className="btn-secondary btn-sm"><Plus size={13} /> Add item</button>
            )}
          </div>
          {addingItem && (
            <form onSubmit={addItem} className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
              <FormRow>
                <Field label="Support item name" required error={itemError}>
                  {(id) => <input id={id} className="input-field" value={itemForm.supportItemName} placeholder="For example, daily living assistance" onChange={(e) => setItem('supportItemName', e.target.value)} />}
                </Field>
                <Field label="Support item number">
                  {(id) => <input id={id} className="input-field" value={itemForm.supportItemNumber} placeholder="For example, 01_011_0107_1_1" onChange={(e) => setItem('supportItemNumber', e.target.value)} />}
                </Field>
              </FormRow>
              <FormRow cols={3}>
                <Field label="Hours per week">
                  {(id) => <input id={id} type="number" step="0.5" className="input-field tabular-nums" value={itemForm.hoursPerWeek} onChange={(e) => setItem('hoursPerWeek', e.target.value)} />}
                </Field>
                <Field label="Rate ($ per hour)">
                  {(id) => <input id={id} type="number" step="0.01" className="input-field tabular-nums" value={itemForm.rate} onChange={(e) => setItem('rate', e.target.value)} />}
                </Field>
                <Field label="Total amount ($)">
                  {(id) => <input id={id} type="number" step="0.01" className="input-field tabular-nums" value={itemForm.totalAmount} onChange={(e) => setItem('totalAmount', e.target.value)} />}
                </Field>
              </FormRow>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setAddingItem(false); setItemError(''); }} className="btn-secondary btn-sm">Cancel</button>
                <button type="submit" disabled={savingItem} className="btn-primary btn-sm">{savingItem && <Loader2 size={12} className="animate-spin" />}Add item</button>
              </div>
            </form>
          )}
          {items.length ? (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th scope="col" className="py-2 px-3 text-left text-[12px] font-semibold text-slate-500">Support item</th>
                    <th scope="col" className="py-2 px-3 text-right text-[12px] font-semibold text-slate-500 whitespace-nowrap">Hours / week</th>
                    <th scope="col" className="py-2 px-3 text-right text-[12px] font-semibold text-slate-500">Rate</th>
                    <th scope="col" className="py-2 px-3 text-right text-[12px] font-semibold text-slate-500">Total</th>
                    <th scope="col" className="w-10"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((it) => (
                    <tr key={it.id}>
                      <td className="py-2 px-3">
                        <div className="text-slate-800">{it.support_item_name || it.support_item_number || '—'}</div>
                        {it.support_item_name && it.support_item_number && <div className="text-[12px] text-slate-500 tabular-nums">{it.support_item_number}</div>}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-700">{it.hours_per_week != null && it.hours_per_week !== '' ? num(it.hours_per_week, 1) : '—'}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-700 whitespace-nowrap">{money(it.rate || 0)}</td>
                      <td className="py-2 px-3 text-right tabular-nums font-medium text-slate-900 whitespace-nowrap">{money(it.total_amount || 0)}</td>
                      <td className="py-1 px-1 text-right">
                        <button type="button" onClick={() => delItem(it)} aria-label={`Remove ${it.support_item_name || 'support item'}`}
                          className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors duration-150"><X size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !addingItem && (
            <p className="text-[13px] text-slate-500">No support items yet. Add the supports this agreement covers.</p>
          )}
        </div>
      </div>
    </DetailDrawer>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function ServiceAgreements() {
  const confirm = useConfirm();
  const [agreements, setAgreements] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [dialog, setDialog] = useState(undefined);  // undefined = closed · null = new · agreement
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    api.get('/service-agreements')
      .then((a) => setAgreements(Array.isArray(a.data) ? a.data : []))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
    api.get('/clients?limit=500').then((c) => setClients((c.data && c.data.clients) || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (a) => {
    try {
      const r = await api.get(`/service-agreements/${a.id}`);
      setDetail(r.data);
    } catch { toast.error('Could not open the agreement'); }
  }, []);
  const refreshDetail = useCallback(async () => {
    if (!detail) return;
    try { const r = await api.get(`/service-agreements/${detail.id}`); setDetail(r.data); } catch { /* keep what is shown */ }
    load();
  }, [detail, load]);

  const closeDialog = useCallback(() => setDialog(undefined), []);
  const closeDetail = useCallback(() => setDetail(null), []);

  const del = async (a) => {
    const ok = await confirm(`Agreement ${a.agreement_number ? `#${a.agreement_number} ` : ''}for ${a.client_name || 'this participant'} is deleted with its support items. This can't be undone.`,
      { title: 'Delete this service agreement?', confirmLabel: 'Delete agreement', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/service-agreements/${a.id}`);
      toast.success('Agreement deleted');
      setDetail(null);
      load();
    } catch { toast.error('Could not delete the agreement'); }
  };

  const downloadRow = async (a) => {
    try {
      const r = await api.get(`/service-agreements/${a.id}`);
      await downloadAgreementPdf(r.data);
    } catch { toast.error('Could not create the PDF'); }
  };

  const counts = useMemo(() => {
    const c = { all: agreements.length, draft: 0, active: 0, expiring: 0, expired: 0, cancelled: 0 };
    agreements.forEach((a) => { c[displayStatus(a)] += 1; });
    return c;
  }, [agreements]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return agreements.filter((a) => (!filter || displayStatus(a) === filter)
      && (!q || [a.client_name, a.agreement_number, AGREEMENT_STATUS[displayStatus(a)].label].some((v) => String(v || '').toLowerCase().includes(q))));
  }, [agreements, search, filter]);

  const chips = [
    { value: null, label: 'All', count: counts.all },
    { value: 'active', label: 'Active', count: counts.active },
    { value: 'expiring', label: 'Expiring', count: counts.expiring, tone: counts.expiring ? 'amber' : undefined },
    { value: 'expired', label: 'Expired', count: counts.expired, tone: counts.expired ? 'rose' : undefined },
    { value: 'draft', label: 'Draft', count: counts.draft },
    counts.cancelled > 0 && { value: 'cancelled', label: 'Cancelled', count: counts.cancelled },
  ].filter(Boolean);

  const columns = [
    {
      key: 'agreement_number', header: 'Agreement', sortable: true, sortValue: (a) => String(a.agreement_number || '').toLowerCase() || null,
      render: (a) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); openDetail(a); }}
          className="font-semibold text-slate-900 hover:text-primary-700 hover:underline underline-offset-2 text-left whitespace-nowrap">
          {a.agreement_number ? `#${a.agreement_number}` : 'No number'}
        </button>
      ),
    },
    {
      key: 'client_name', header: 'Participant', sortable: true,
      render: (a) => (
        <Link to={`/clients/${a.client_id}`} onClick={(e) => e.stopPropagation()} className="text-primary-600 hover:underline whitespace-nowrap">
          {a.client_name}
        </Link>
      ),
    },
    {
      key: 'period', header: 'Period', sortable: true, sortValue: (a) => ymd(a.end_date) || null,
      render: (a) => {
        const st = displayStatus(a);
        const ends = st === 'active' || st === 'expiring' ? endsText(a) : null;
        return (
          <div className="leading-tight whitespace-nowrap">
            <div className="tabular-nums text-slate-800">{periodText(a)}</div>
            {ends && <div className={`text-[12px] ${ends.tone}`}>{ends.text}</div>}
          </div>
        );
      },
    },
    { key: 'total_value', header: 'Total value', money: true, sortable: true, sortValue: (a) => Number(a.total_value) || 0 },
    {
      key: 'status', header: 'Status', sortable: true, sortValue: (a) => displayStatus(a),
      render: (a) => <StatusPill status={displayStatus(a)} map={AGREEMENT_STATUS} />,
    },
    {
      key: 'signed', header: 'Signed', sortable: true, sortValue: (a) => (a.signed_by_client ? ymd(a.signed_date) || '1' : null),
      render: (a) => <span className={a.signed_by_client ? 'text-slate-700 whitespace-nowrap' : 'text-slate-500 whitespace-nowrap'}>{signedText(a)}</span>,
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', width: '3.5rem',
      render: (a) => (
        <OverflowMenu label={`Actions for agreement ${a.agreement_number || a.client_name}`} size={16} className="p-2.5" items={[
          { label: 'Open', icon: FileText, onClick: () => openDetail(a) },
          { label: 'Edit', icon: Pencil, onClick: () => setDialog(a) },
          { label: 'Download PDF', icon: Download, onClick: () => downloadRow(a) },
          { divider: true },
          { label: 'Delete', icon: Trash2, danger: true, onClick: () => del(a) },
        ]} />
      ),
    },
  ];

  const filtersOn = !!filter || !!search.trim();

  return (
    <div className={PAGE_WIDE}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center min-w-0">
          <SearchInput value={search} onChange={setSearch} placeholder="Search participant, number or status" className="w-full sm:w-72" />
          <span className="text-[13px] text-slate-500 tabular-nums whitespace-nowrap" aria-live="polite">
            {loading ? 'Loading…' : filtersOn ? `${num(rows.length)} of ${plural(agreements.length, 'agreement')}` : plural(agreements.length, 'agreement')}
          </span>
        </div>
        <button type="button" onClick={() => setDialog(null)} className="btn-primary self-start lg:self-auto"><Plus size={16} /> New agreement</button>
      </div>

      <FilterChips multi={false} ariaLabel="Filter agreements" value={filter} onChange={setFilter} options={chips} />

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <DataTable
          label="Service agreements"
          columns={columns}
          rows={rows}
          loading={loading}
          error={loadError ? "We couldn't load service agreements. Check your connection, then try again." : null}
          onRetry={load}
          stickyHeader={false}
          onRowClick={openDetail}
          empty={agreements.length === 0 ? {
            icon: FileText, title: 'No service agreements yet',
            hint: 'Record each participant’s service agreement so its period, value and signature are tracked.',
            action: <button type="button" onClick={() => setDialog(null)} className="btn-primary btn-sm"><Plus size={14} /> New agreement</button>,
          } : {
            icon: FileText, title: 'No agreements match these filters',
            action: <button type="button" onClick={() => { setFilter(null); setSearch(''); }} className="btn-secondary btn-sm">Clear filters</button>,
          }}
        />
      </div>

      {dialog !== undefined && (
        <AgreementDialog agreement={dialog} clients={clients} onClose={closeDialog}
          onSaved={() => { load(); if (detail && dialog && detail.id === dialog.id) refreshDetail(); }} />
      )}
      {detail && (
        <AgreementDrawer agreement={detail} onClose={closeDetail}
          onEdit={() => { setDialog(detail); setDetail(null); }}
          onDelete={() => del(detail)}
          onChanged={refreshDetail} />
      )}
    </div>
  );
}
